import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import {
  type FunctionComponent,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { accountAtom } from "shared/lib/account";

import { appAtom } from "scmp-app/lib/app/atoms";
import type { batchQueryAbWidgetQuery } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";

import { fetchAiWidgetRecommendations, getColdStart, massageApiResponse } from "./api";
import { AiWidgetBatchQuery } from "./batch-query";
import { AI_WIDGET_FETCH_LOG_PREFIX } from "./constants";
import { AiWidgetErrorBoundary } from "./error-boundary";
import { AiWidgetFallbackLoader } from "./fallback-query-loader";
import type { AiWidgetEntityIds } from "./types";

// Slot keys for the four mid-page widgets. CFT/spotlight are always AI-driven;
// story-package slots 1/2 can be overridden to the CMS queues by app-config.
export type AiSlotKey = "cft" | "spotlight" | "storyPackageOne" | "storyPackageTwo";

export type FallbackQueryVariables = {
  chinaFutureTechSectionEntityId: string;
  chinaFutureTechStoryPackageArticlesQueueName: string;
  includeStoryPackageOne: boolean;
  includeStoryPackageTwo: boolean;
  /** CMS queue name for the story-package-1 slot: `homepage_package_top_{edition}`. */
  packageTopStoryPackageArticlesQueueName: string;
  /** CMS queue name for the story-package-2 slot: `homepage_package_top_2_{edition}`. */
  spotlightQueueName: string;
  storyPackageTwoQueueName: string;
};

export type FallbackReason =
  | "api_error"
  | "api_timeout"
  | "empty_entity_ids"
  | "render_error"
  | "vi_timeout";

export type WidgetVersion = "cold_start" | "qualified_reader";

type BatchData = batchQueryAbWidgetQuery["response"];

type Props = {
  fallbackQueryVariables: FallbackQueryVariables;
  onReady: (entityIds: string[]) => void;
  renderPlaceholders: () => ReactNode;
  renderWidgets: (
    batchData: BatchData | null,
    fallbackData: unknown,
    slotModes: SlotModes,
    topicNames: {
      cftTopicName?: string;
      storyPackageOneTopicName?: string;
      storyPackageTwoTopicName?: string;
    },
    fallbackReason?: FallbackReason,
    widgetVersion?: WidgetVersion,
    recommendationId?: string,
  ) => ReactNode;
  ssrContentEntityIds: string[];
  useAiForStoryPackageOneSlot: boolean;
  useAiForStoryPackageTwoSlot: boolean;
};

type SlotModes = Record<AiSlotKey, "ai" | "queue">;

const FULL_QUEUE_SLOT_MODES: SlotModes = {
  cft: "queue",
  spotlight: "queue",
  storyPackageOne: "queue",
  storyPackageTwo: "queue",
};

const hasEmptyEntityIds = (
  result: AiWidgetEntityIds,
  useAiForStoryPackageOneSlot: boolean,
  useAiForStoryPackageTwoSlot: boolean,
): boolean =>
  (useAiForStoryPackageOneSlot && result.storyPackageOne.length === 0) ||
  (useAiForStoryPackageTwoSlot && result.storyPackageTwo.length === 0) ||
  result.cft.length === 0 ||
  result.spotlight.length === 0;

const collectAllEntityIds = (
  result: AiWidgetEntityIds,
  useAiForStoryPackageOneSlot: boolean,
  useAiForStoryPackageTwoSlot: boolean,
): string[] => [
  ...(useAiForStoryPackageOneSlot ? result.storyPackageOne : []),
  ...(useAiForStoryPackageTwoSlot ? result.storyPackageTwo : []),
  ...result.cft,
  ...result.spotlight,
];

const getWidgetVersion = (
  coldStart: boolean,
  fallbackToQueues: boolean,
): undefined | WidgetVersion => {
  if (fallbackToQueues) return undefined;
  return coldStart ? "cold_start" : "qualified_reader";
};

/**
 * Renders the homepage mid-page AI-personalised widget block (rolled out from
 * the HomeMid A/B test's winning Variant 2). Always fetches AI recommendations
 * and renders the treatment widgets; on any failure falls back to queue-driven
 * widgets via `renderFallbackWidgets`.
 *
 * SCMPWEB-9641: story-package slots 1 (queue `homepage_package_top_{edition}`)
 * and 2 (queue `homepage_package_top_2_{edition}`) can be overridden to the
 * CMS node queues per-edition via the
 * `homepage_mid_page_recommendation` app-config — `useAiForStoryPackageOneSlot` /
 * `useAiForStoryPackageTwoSlot` false means that slot renders queue content
 * even when the AI path succeeds.
 */
export const AiWidgetFetch: FunctionComponent<Props> = ({
  fallbackQueryVariables,
  onReady,
  renderPlaceholders,
  renderWidgets,
  ssrContentEntityIds,
  useAiForStoryPackageOneSlot,
  useAiForStoryPackageTwoSlot,
}) => {
  const [apiResult, setApiResult] = useState<AiWidgetEntityIds | null>(null);
  const [coldStart, setColdStart] = useState(false);
  const [fallbackToQueues, setFallbackToQueues] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<FallbackReason | undefined>(undefined);
  const fetchStartedReference = useRef(false);
  const onReadyFiredReference = useRef(false);

  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { user } = useAtomValue(accountAtom);

  const fireOnReady = useCallback(
    (entityIds: string[]) => {
      if (onReadyFiredReference.current) return;
      onReadyFiredReference.current = true;
      onReady(entityIds);
    },
    [onReady],
  );

  // Queue-driven data for any slot overridden by config (SCMPWEB-9641).
  const needsForcedFallback = !useAiForStoryPackageOneSlot || !useAiForStoryPackageTwoSlot;
  const forcedFallbackVariables: FallbackQueryVariables = {
    ...fallbackQueryVariables,
    includeStoryPackageOne: !useAiForStoryPackageOneSlot,
    includeStoryPackageTwo: !useAiForStoryPackageTwoSlot,
  };

  // Merge entity IDs from the AI batch (AI-driven slots) and the forced
  // queue loader (config-overridden slots), then fire onReady once both
  // sides have contributed (or are known absent). cft/spotlight are always
  // AI-driven, so the AI side is always expected to report.
  const aiEntityIdsReference = useRef<null | string[]>(null);
  const forcedEntityIdsReference = useRef<null | string[]>(null);

  const flushOnReady = useCallback(() => {
    if (aiEntityIdsReference.current === null) return;
    if (needsForcedFallback && forcedEntityIdsReference.current === null) return;

    const aiIds = aiEntityIdsReference.current;
    const forcedIds = forcedEntityIdsReference.current ?? [];
    fireOnReady([...aiIds, ...forcedIds]);
  }, [fireOnReady, needsForcedFallback]);

  const handleAiRenderError = useCallback(() => {
    console.error(
      `${AI_WIDGET_FETCH_LOG_PREFIX} AI rendering error — falling back to queue-driven widgets.`,
    );
    setFallbackReason("render_error");
    setFallbackToQueues(true);
  }, []);

  const widgetVersion = getWidgetVersion(coldStart, fallbackToQueues);

  const renderFallbackPath = useCallback(
    () => (
      <AiWidgetFallbackLoader
        onEntityIds={fireOnReady}
        renderPlaceholders={renderPlaceholders}
        variables={fallbackQueryVariables}
      >
        {data => renderWidgets(null, data, FULL_QUEUE_SLOT_MODES, {}, fallbackReason)}
      </AiWidgetFallbackLoader>
    ),
    [fallbackQueryVariables, fallbackReason, fireOnReady, renderPlaceholders, renderWidgets],
  );

  // Fetch AI recommendations once on mount — wait until navigatorInfo has
  // loaded so reqRegion carries the user's actual country instead of the
  // "global" fallback.
  useEffect(() => {
    if (!navigatorInfoLoaded) return;
    if (fetchStartedReference.current) return;
    fetchStartedReference.current = true;

    const VI_POLL_INTERVAL_MS = 1000;
    const VI_POLL_TIMEOUT_MS = 5000;

    const fetchApi = async (visitorId: string) => {
      try {
        const response = await fetchAiWidgetRecommendations({
          excludeArticleIds: ssrContentEntityIds,
          reqRegion: navigatorInfo?.country ?? "global",
          userId: user?.uuid,
          visitorId,
        });
        const entityIds = massageApiResponse(response);
        const apiColdStart = getColdStart(response);
        setColdStart(apiColdStart);
        setApiResult(entityIds);
      } catch (error) {
        const isTimeout = error instanceof DOMException && error.name === "AbortError";
        const reason = isTimeout ? "api_timeout" : "api_error";
        console.error(
          `${AI_WIDGET_FETCH_LOG_PREFIX} API fetch failed (${reason}) — falling back to queue-driven widgets.`,
          error,
        );
        setFallbackReason(reason);
        setFallbackToQueues(true);
      }
    };

    const viBeforeFetch = Cookies.get("_vi") ?? "";

    let poll: ReturnType<typeof setInterval> | undefined;

    if (viBeforeFetch) {
      void fetchApi(viBeforeFetch);
    } else {
      let elapsed = 0;
      poll = setInterval(() => {
        elapsed += VI_POLL_INTERVAL_MS;
        const viNow = Cookies.get("_vi");
        if (viNow) {
          clearInterval(poll);
          void fetchApi(viNow);
        } else if (elapsed >= VI_POLL_TIMEOUT_MS) {
          clearInterval(poll);
          setFallbackReason("vi_timeout");
          setFallbackToQueues(true);
        }
      }, VI_POLL_INTERVAL_MS);
    }

    return () => {
      if (poll) clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigatorInfoLoaded]);

  // Fire onReady once the AI result is available and we're rendering the AI
  // widgets (not falling back to the queue-driven path)
  useEffect(() => {
    if (!apiResult) return;
    if (fallbackToQueues) return;
    if (hasEmptyEntityIds(apiResult, useAiForStoryPackageOneSlot, useAiForStoryPackageTwoSlot)) {
      console.warn(
        `${AI_WIDGET_FETCH_LOG_PREFIX} Some widgets have empty entityIds — falling back to queue-driven widgets.`,
        apiResult,
      );
      setFallbackReason("empty_entity_ids");
      setFallbackToQueues(true);
      return;
    }

    aiEntityIdsReference.current = collectAllEntityIds(
      apiResult,
      useAiForStoryPackageOneSlot,
      useAiForStoryPackageTwoSlot,
    );
    flushOnReady();
  }, [
    apiResult,
    fallbackToQueues,
    flushOnReady,
    useAiForStoryPackageTwoSlot,
    useAiForStoryPackageOneSlot,
  ]);

  // Any error or fallback → queue-driven widgets
  if (fallbackToQueues) {
    return <>{renderFallbackPath()}</>;
  }

  if (!apiResult) {
    return <>{renderPlaceholders()}</>;
  }

  if (hasEmptyEntityIds(apiResult, useAiForStoryPackageOneSlot, useAiForStoryPackageTwoSlot)) {
    return <>{renderPlaceholders()}</>;
  }

  const slotModes: SlotModes = {
    cft: "ai",
    spotlight: "ai",
    storyPackageOne: useAiForStoryPackageOneSlot ? "ai" : "queue",
    storyPackageTwo: useAiForStoryPackageTwoSlot ? "ai" : "queue",
  };

  // Zero out slots that the config forces to queues so the batch query skips
  // them via @include(if:).
  const batchStoryPackageOneIds = useAiForStoryPackageOneSlot ? apiResult.storyPackageOne : [];
  const batchStoryPackageOneTopicId = useAiForStoryPackageOneSlot
    ? apiResult.storyPackageOneTopicEntityId
    : undefined;
  const batchStoryPackageTwoIds = useAiForStoryPackageTwoSlot ? apiResult.storyPackageTwo : [];
  const batchStoryPackageTwoTopicId = useAiForStoryPackageTwoSlot
    ? apiResult.storyPackageTwoTopicEntityId
    : undefined;

  const aiBatch = (fallbackData: unknown) => (
    <AiWidgetErrorBoundary onError={handleAiRenderError}>
      <Suspense fallback={<>{renderPlaceholders()}</>}>
        <AiWidgetBatchQuery
          cftEntityIds={apiResult.cft}
          cftTopicEntityId={apiResult.cftTopicEntityId}
          spotlightEntityIds={apiResult.spotlight}
          storyPackageOneEntityIds={batchStoryPackageOneIds}
          storyPackageOneTopicEntityId={batchStoryPackageOneTopicId}
          storyPackageTwoEntityIds={batchStoryPackageTwoIds}
          storyPackageTwoTopicEntityId={batchStoryPackageTwoTopicId}
        >
          {data =>
            renderWidgets(
              data,
              fallbackData,
              slotModes,
              {
                cftTopicName: apiResult.cftTopicName,
                storyPackageOneTopicName: apiResult.storyPackageOneTopicName,
                storyPackageTwoTopicName: apiResult.storyPackageTwoTopicName,
              },
              undefined,
              widgetVersion,
              apiResult.recommendationId,
            )
          }
        </AiWidgetBatchQuery>
      </Suspense>
    </AiWidgetErrorBoundary>
  );

  // No forced slots → plain AI path (no queue data needed).
  if (!needsForcedFallback) {
    return aiBatch(undefined);
  }

  // At least one slot is config-forced to queue: mount the fallback loader so
  // the parent's render prop reads edges/topics for those slots from the
  // fallback query. Slots still on AI render from the batch query.
  return (
    <AiWidgetFallbackLoader
      onEntityIds={ids => {
        forcedEntityIdsReference.current = ids;
        flushOnReady();
      }}
      renderPlaceholders={renderPlaceholders}
      variables={forcedFallbackVariables}
    >
      {fallbackData => aiBatch(fallbackData)}
    </AiWidgetFallbackLoader>
  );
};

AiWidgetFetch.displayName = "AiWidgetFetch";
